import { LanguageSelect, ProcessingOverlay, SettingsSection } from '@components'

import { Color, FormItem as FormItemDS, FormItemsGroup, NavItem, Select, Tag } from '@billy/design-system'
import React, { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useCurrentUser } from '@modules-deprecated/app/user'

import { CustomEvent } from '../../../../../../enums/customEvent'
import { useSegment } from '../../../../../../hooks'
import { LocaleIdentifier } from '../../../../../../types/localeIdentifier'
import { ThemeLayoutMode } from '../../../../../../types/themeLayoutMode'
import { dispatchCustomEvent } from '../../../../../../utils/customEvent'
import { useLanguageChange } from '../../hooks/useLanguageChange'
import { useThemeLayoutModeChange } from '../../hooks/useThemeLayoutModeChange'
import * as Styled from './styles'

export const UserProfileOtherSettings = (): ReactElement => {
  const { isLoading: isLoadingOrganization } = useUserOrganization()
  const { changeLanguage } = useLanguageChange()
  const { changeThemeLayoutMode } = useThemeLayoutModeChange()
  const { user, isLoading: isLoadingCurrentUser } = useCurrentUser()
  const { track } = useSegment()
  const { t } = useTranslation()

  const isLoading = isLoadingOrganization || isLoadingCurrentUser

  const themeItems: NavItem<ThemeLayoutMode>[] = useMemo(
    () => [
      {
        id: 'light',
        value: 'light',
        children: t('settings.user.theme.light'),
      },
      {
        id: 'dark',
        value: 'dark',
        children: t('settings.user.theme.dark'),
        rightDecoration: <Tag>{t('beta')}</Tag>,
      },
      {
        id: 'auto',
        value: 'auto',
        children: t('settings.user.theme.auto'),
      },
    ],
    [t],
  )

  const handleThemeChange = useCallback(
    (_id?: string, value?: ThemeLayoutMode) => {
      if (value) {
        changeThemeLayoutMode(value)
        dispatchCustomEvent<ThemeLayoutMode>(CustomEvent.ThemeLayoutModeChanged, value)
        track('xxx - Bookkeeping - Theme change', { newThemeMode: value })
      }
    },
    [changeThemeLayoutMode, track],
  )

  return (
    <SettingsSection>
      <SettingsSection.Content title={t('settings.user.other_settings')}>
        <Styled.FormFieldsWrapper>
          {isLoading && <ProcessingOverlay color={Color.White} />}
          <FormItemsGroup>
            <FormItemDS label={t('locale.your_language')}>
              <LanguageSelect
                onSelect={(value) => changeLanguage(value as LocaleIdentifier)}
                selectedId={user?.locale}
                withSearch={false}
              />
            </FormItemDS>
            <FormItemDS label={t('settings.user.theme.label')}>
              <Select<ThemeLayoutMode>
                items={themeItems}
                placement="bottom-start"
                onSelect={handleThemeChange}
                selectedId={user?.themeMode || ''}
                withSearch={false}
                dropdownSize="fitTrigger"
              />
            </FormItemDS>
          </FormItemsGroup>
        </Styled.FormFieldsWrapper>
      </SettingsSection.Content>
    </SettingsSection>
  )
}
